import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { listS3Files, generatePresignedUrl, deleteFromS3, renameFileInS3, updateFileMetadata } from '../utils/s3Utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiFile, FiImage, FiVideo, FiMusic, FiDownload, FiTrash2, FiEye, FiFolder, 
  FiEdit3, FiCopy, FiExternalLink, FiX, FiCheck, FiSave, FiRefreshCw, FiAlertCircle, FiLoader 
} = FiIcons;

function FileList({ files, onDelete, refreshTrigger }) {
  const { user } = useAuth();
  const [allFiles, setAllFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFromS3, setLoadingFromS3] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'gallery'
  const [editingFile, setEditingFile] = useState(null);
  const [fileMetadata, setFileMetadata] = useState({});
  const [updatingFile, setUpdatingFile] = useState(false);
  const [error, setError] = useState(null);
  const [s3DebugInfo, setS3DebugInfo] = useState(null);

  // Load files from S3 on component mount and when refreshTrigger changes
  useEffect(() => {
    loadFilesFromS3();
  }, [refreshTrigger, user.s3Folder]);

  const loadFilesFromS3 = async () => {
    if (!user.s3Folder) {
      console.error('❌ No S3 folder configured for user');
      setError('No S3 folder configured for this user. Please contact administrator.');
      setLoading(false);
      return;
    }

    setLoadingFromS3(true);
    setError(null);
    setS3DebugInfo(null);
    
    try {
      console.log(`📂 Loading files from S3 folder: ${user.s3Folder}`);
      
      const s3Files = await listS3Files(user.s3Folder);
      
      // Merge S3 files with any locally uploaded files
      const mergedFiles = [...s3Files, ...files];
      
      // Remove duplicates based on file key
      const uniqueFiles = mergedFiles.reduce((acc, file) => {
        const existingIndex = acc.findIndex(f => f.key === file.key);
        if (existingIndex === -1) {
          acc.push(file);
        }
        return acc;
      }, []);
      
      setAllFiles(uniqueFiles);
      setS3DebugInfo({
        s3FilesCount: s3Files.length,
        localFilesCount: files.length,
        totalFiles: uniqueFiles.length,
        folder: user.s3Folder
      });
      
      console.log(`✅ Successfully loaded ${s3Files.length} files from S3`);
    } catch (error) {
      console.error('❌ Error loading files from S3:', error);
      
      // Store detailed error information
      setS3DebugInfo({
        error: error.message,
        folder: user.s3Folder,
        timestamp: new Date().toISOString(),
        environment: {
          hasAccessKey: !!process.env.REACT_APP_AWS_ACCESS_KEY_ID,
          hasSecretKey: !!process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
          hasBucket: !!process.env.REACT_APP_S3_BUCKET,
          hasRegion: !!process.env.REACT_APP_AWS_REGION,
          bucket: process.env.REACT_APP_S3_BUCKET,
          region: process.env.REACT_APP_AWS_REGION
        }
      });
      
      setError(`Failed to load files from S3: ${error.message}`);
      
      // Fall back to mock data if S3 fails
      const mockFiles = [
        {
          id: 1,
          name: 'project-proposal.pdf',
          originalName: 'project-proposal.pdf',
          size: 2048576,
          type: 'application/pdf',
          uploadedAt: new Date(Date.now() - 86400000),
          url: 'https://picsum.photos/seed/proposal/800/600.jpg',
          key: `${user.s3Folder}/project-proposal.pdf`,
          metadata: {
            title: 'Project Proposal Document',
            description: 'Q4 2024 project proposal with budget estimates',
            tags: ['proposal', 'q4-2024', 'budget'],
            alt: 'Project proposal document with financial details'
          }
        },
        {
          id: 2,
          name: 'banner-image.jpg',
          originalName: 'banner-image.jpg',
          size: 1536000,
          type: 'image/jpeg',
          uploadedAt: new Date(Date.now() - 172800000),
          url: 'https://picsum.photos/seed/banner/800/600.jpg',
          key: `${user.s3Folder}/banner-image.jpg`,
          metadata: {
            title: 'Website Banner',
            description: 'Main banner image for homepage',
            tags: ['banner', 'homepage', 'marketing'],
            alt: 'Colorful website banner with company branding'
          }
        },
        {
          id: 3,
          name: 'presentation.mp4',
          originalName: 'presentation.mp4',
          size: 15728640,
          type: 'video/mp4',
          uploadedAt: new Date(Date.now() - 259200000),
          url: 'https://picsum.photos/seed/presentation/800/600.jpg',
          key: `${user.s3Folder}/presentation.mp4`,
          metadata: {
            title: 'Product Presentation',
            description: 'Product features and benefits presentation',
            tags: ['presentation', 'product', 'demo'],
            alt: 'Video presentation showcasing product features'
          }
        },
        {
          id: 4,
          name: 'team-photo.jpg',
          originalName: 'team-photo.jpg',
          size: 896000,
          type: 'image/jpeg',
          uploadedAt: new Date(Date.now() - 345600000),
          url: 'https://picsum.photos/seed/team/800/600.jpg',
          key: `${user.s3Folder}/team-photo.jpg`,
          metadata: {
            title: 'Team Photo',
            description: 'Annual team building event photo',
            tags: ['team', 'event', 'culture'],
            alt: 'Team members gathered at company event'
          }
        }
      ];
      
      setAllFiles([...mockFiles, ...files]);
    } finally {
      setLoading(false);
      setLoadingFromS3(false);
    }
  };

  const getFileIcon = (type) => {
    const mainType = type.split('/')[0];
    switch (mainType) {
      case 'image':
        return FiImage;
      case 'video':
        return FiVideo;
      case 'audio':
        return FiMusic;
      default:
        return FiFile;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePreview = (file) => {
    setSelectedFile(file);
  };

  const handleEdit = (file) => {
    setEditingFile(file);
    setFileMetadata({
      name: file.name,
      originalName: file.originalName,
      title: file.metadata?.title || '',
      description: file.metadata?.description || '',
      tags: file.metadata?.tags?.join(',') || '',
      alt: file.metadata?.alt || ''
    });
  };

  const handleSaveFileChanges = async () => {
    if (!editingFile) return;
    
    setUpdatingFile(true);
    try {
      // Update local state first for immediate feedback
      const updatedFile = {
        ...editingFile,
        name: fileMetadata.name,
        metadata: {
          title: fileMetadata.title,
          description: fileMetadata.description,
          tags: fileMetadata.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          alt: fileMetadata.alt
        }
      };
      
      setAllFiles(prev => prev.map(file => 
        file.id === editingFile.id ? updatedFile : file
      ));
      
      // Update metadata on server (in production, this would update a database)
      try {
        await updateFileMetadata(editingFile.key, updatedFile.metadata);
      } catch (error) {
        console.warn('Metadata update failed, but local state updated:', error);
      }
      
      // If file name changed, rename on S3
      if (fileMetadata.name !== editingFile.name) {
        try {
          const fileExtension = editingFile.name.split('.').pop();
          const newKey = `${user.s3Folder}/${fileMetadata.name}`;
          await renameFileInS3(editingFile.key, newKey);
          
          // Update the file key and URL in local state
          const finalUpdatedFile = {
            ...updatedFile,
            key: newKey,
            url: editingFile.url.replace(editingFile.key, newKey)
          };
          
          setAllFiles(prev => prev.map(file => 
            file.id === editingFile.id ? finalUpdatedFile : file
          ));
        } catch (error) {
          console.warn('File rename failed on server, but local state updated:', error);
        }
      }
      
      setEditingFile(null);
      setFileMetadata({});
    } catch (error) {
      console.error('Failed to update file:', error);
      alert('Failed to update file. Please try again.');
    } finally {
      setUpdatingFile(false);
    }
  };

  const handleDownload = async (file) => {
    if (downloading[file.id]) return;
    
    setDownloading(prev => ({ ...prev, [file.id]: true }));
    try {
      // Generate presigned URL for secure download
      const downloadUrl = file.url !== '#' ? file.url : await generatePresignedUrl(file.key);
      
      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [file.id]: false }));
    }
  };

  const handleDelete = async (fileId) => {
    const file = allFiles.find(f => f.id === fileId);
    if (!file) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete ${file.name}?`);
    if (!confirmed) return;
    
    try {
      // Delete from S3 if we have a key
      if (file.key && file.key !== '#') {
        await deleteFromS3(file.key);
      }
      
      setAllFiles(prev => prev.filter(f => f.id !== fileId));
      onDelete(fileId);
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const handleRefresh = () => {
    loadFilesFromS3();
  };

  const isImageFile = (type) => type.startsWith('image/');

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiFolder} className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Your Files</h2>
          </div>
          <div className="animate-pulse">
            <div className="h-8 w-20 bg-gray-300 rounded"></div>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                <div className="w-10 h-10 bg-gray-300 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.2 }} 
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiFolder} className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Your Files</h2>
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full">
              {allFiles.length}
            </span>
            {loadingFromS3 && (
              <div className="flex items-center space-x-2 text-sm text-blue-600">
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                <span>Loading from S3...</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {/* Refresh Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleRefresh}
              disabled={loadingFromS3}
              className="p-2 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
              title="Refresh from S3"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${loadingFromS3 ? 'animate-spin' : ''}`} />
            </motion.button>
            
            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <SafeIcon icon={FiFolder} className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode('gallery')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'gallery' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <SafeIcon icon={FiImage} className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4"
          >
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">S3 Connection Error</p>
                <p className="text-sm text-red-600">{error}</p>
                {s3DebugInfo && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-500 cursor-pointer hover:text-red-700">
                      View Debug Information
                    </summary>
                    <pre className="text-xs text-red-600 mt-2 bg-red-100 p-2 rounded overflow-auto">
                      {JSON.stringify(s3DebugInfo, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {allFiles.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiFolder} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No files yet</h3>
            <p className="text-gray-500">Upload your first file to get started</p>
          </div>
        ) : (
          <>
            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-3">
                <AnimatePresence>
                  {allFiles.map((file) => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                          <SafeIcon icon={getFileIcon(file.type)} className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>{formatFileSize(file.size)}</span>
                            <span>•</span>
                            <span>{formatDistanceToNow(new Date(file.uploadedAt), { addSuffix: true })}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handlePreview(file)}
                          className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Preview"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEdit(file)}
                          className="p-2 text-gray-400 hover:text-yellow-600 rounded-lg hover:bg-yellow-50 transition-colors"
                          title="Edit"
                        >
                          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDownload(file)}
                          disabled={downloading[file.id]}
                          className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
                          title="Download"
                        >
                          <SafeIcon icon={FiDownload} className={`w-4 h-4 ${downloading[file.id] ? 'animate-spin' : ''}`} />
                        </motion.button>
                        {user.permissions.includes('delete') && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDelete(file.id)}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </motion.button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Gallery View */}
            {viewMode === 'gallery' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence>
                  {allFiles.map((file) => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      whileHover={{ y: -4 }}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                      onClick={() => handlePreview(file)}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-video bg-gray-100 relative overflow-hidden">
                        {isImageFile(file.type) ? (
                          <img 
                            src={file.url} 
                            alt={file.metadata?.alt || file.name} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" 
                            loading="lazy" 
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <SafeIcon icon={getFileIcon(file.type)} className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                          <SafeIcon icon={FiEye} className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      {/* File Info */}
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 truncate mb-1" title={file.name}>
                          {file.name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2">
                          {formatFileSize(file.size)} • {formatDistanceToNow(new Date(file.uploadedAt), { addSuffix: true })}
                        </p>
                        {/* Tags */}
                        {file.metadata?.tags && file.metadata.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {file.metadata.tags.slice(0, 2).map((tag, index) => (
                              <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {tag}
                              </span>
                            ))}
                            {file.metadata.tags.length > 2 && (
                              <span className="text-xs text-gray-400">+{file.metadata.tags.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* File Preview Modal */}
      <AnimatePresence>
        {selectedFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedFile(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">{selectedFile.name}</h3>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Preview */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Preview</h4>
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      {isImageFile(selectedFile.type) ? (
                        <img 
                          src={selectedFile.url} 
                          alt={selectedFile.metadata?.alt || selectedFile.name} 
                          className="w-full h-full object-contain" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <SafeIcon icon={getFileIcon(selectedFile.type)} className="w-16 h-16 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Details */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">File Details</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-gray-500">File Name</label>
                        <p className="font-medium text-gray-900">{selectedFile.name}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">File Size</label>
                        <p className="font-medium text-gray-900">{formatFileSize(selectedFile.size)}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">File Type</label>
                        <p className="font-medium text-gray-900">{selectedFile.type}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Uploaded</label>
                        <p className="font-medium text-gray-900">
                          {formatDistanceToNow(new Date(selectedFile.uploadedAt), { addSuffix: true })}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">URL</label>
                        <div className="flex items-center space-x-2 mt-1">
                          <input 
                            type="text" 
                            value={selectedFile.url} 
                            readOnly 
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono" 
                          />
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => copyToClipboard(selectedFile.url)}
                            className="p-2 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            <SafeIcon icon={copiedUrl === selectedFile.url ? FiCheck : FiCopy} className="w-4 h-4" />
                          </motion.button>
                          <motion.a
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            href={selectedFile.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-500 hover:text-green-600 rounded-lg hover:bg-green-50 transition-colors"
                            title="Open in new tab"
                          >
                            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                          </motion.a>
                        </div>
                      </div>
                      {/* SEO Metadata */}
                      {selectedFile.metadata && (
                        <div>
                          <label className="text-sm text-gray-500">SEO Metadata</label>
                          <div className="mt-2 space-y-2">
                            {selectedFile.metadata.title && (
                              <div>
                                <p className="text-xs text-gray-400">Title</p>
                                <p className="text-sm text-gray-900">{selectedFile.metadata.title}</p>
                              </div>
                            )}
                            {selectedFile.metadata.description && (
                              <div>
                                <p className="text-xs text-gray-400">Description</p>
                                <p className="text-sm text-gray-900">{selectedFile.metadata.description}</p>
                              </div>
                            )}
                            {selectedFile.metadata.alt && (
                              <div>
                                <p className="text-xs text-gray-400">Alt Text</p>
                                <p className="text-sm text-gray-900">{selectedFile.metadata.alt}</p>
                              </div>
                            )}
                            {selectedFile.metadata.tags && selectedFile.metadata.tags.length > 0 && (
                              <div>
                                <p className="text-xs text-gray-400">Tags</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {selectedFile.metadata.tags.map((tag, index) => (
                                    <span key={index} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="pt-4 border-t border-gray-200 space-y-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleEdit(selectedFile)}
                          className="w-full flex items-center justify-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors"
                        >
                          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                          <span>Edit File Details</span>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleDownload(selectedFile)}
                          disabled={downloading[selectedFile.id]}
                          className="w-full flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <SafeIcon icon={FiDownload} className={`w-4 h-4 ${downloading[selectedFile.id] ? 'animate-spin' : ''}`} />
                          <span>{downloading[selectedFile.id] ? 'Downloading...' : 'Download File'}</span>
                        </motion.button>
                        {user.permissions.includes('delete') && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              handleDelete(selectedFile.id);
                              setSelectedFile(null);
                            }}
                            className="w-full flex items-center justify-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            <span>Delete File</span>
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit File Modal */}
      <AnimatePresence>
        {editingFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setEditingFile(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Edit File Details</h3>
                <button
                  onClick={() => setEditingFile(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveFileChanges();
                  }}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      File Name
                    </label>
                    <input
                      type="text"
                      value={fileMetadata.name}
                      onChange={(e) => setFileMetadata(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Title
                    </label>
                    <input
                      type="text"
                      value={fileMetadata.title}
                      onChange={(e) => setFileMetadata(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter SEO title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={fileMetadata.description}
                      onChange={(e) => setFileMetadata(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter file description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alt Text (for images)
                    </label>
                    <textarea
                      value={fileMetadata.alt}
                      onChange={(e) => setFileMetadata(prev => ({ ...prev, alt: e.target.value }))}
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Describe the image for accessibility"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={fileMetadata.tags}
                      onChange={(e) => setFileMetadata(prev => ({ ...prev, tags: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter tags separated by commas"
                    />
                    <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
                  </div>
                  <div className="pt-6 border-t border-gray-200">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">⚠️ File Rename Information</h4>
                      <p className="text-xs text-yellow-700">
                        Changing the file name will rename the file on the S3 server. The upload date and time will remain unchanged. 
                        The original file name "{editingFile.originalName}" will be preserved in our records.
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={updatingFile}
                        className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <SafeIcon icon={updatingFile ? FiRefreshCw : FiSave} className={`w-4 h-4 ${updatingFile ? 'animate-spin' : ''}`} />
                        <span>{updatingFile ? 'Saving...' : 'Save Changes'}</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => setEditingFile(null)}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
                      >
                        Cancel
                      </motion.button>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default FileList;